import { Play } from '@phosphor-icons/react';
import { Button } from '@whistler-ui/k2/components/Button';
import AndroidLogo from '@whistler-ui/k2/components/CustomIcons/AndroidLogo';
import { AppleLogo } from '@whistler-ui/k2/components/CustomIcons/AppleLogo';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';

import {
  ANDROID_SERVICE,
  APPLE,
  CANCELLED,
  CREATED,
  PENDING,
  RUNNING,
  USE_CASE,
} from '../../../constants';
import { getDeviceAndTitleFromTestCase } from '../../../helpers/all';
import {
  useGetApiTestExecution,
  useGetUseCaseTestsExecution,
} from '../../../hooks';
import { useTestExecutionsStore } from '../../../store/testExecutions';
import { useThemeStore } from '../../../store/theme';
import { ApiTestExecution, TUseCaseType } from '../../../types';
import { useWebsheetPopUp } from '../hooks/useWebsheetPopUp';
import { TestIcon } from './TestIcon';

const ICON_SIZE = 24;
const getDeviceIcon = deviceKey => {
  switch (deviceKey) {
    case APPLE:
      return <AppleLogo size={ICON_SIZE} fill="#3A474E" />;
    case ANDROID_SERVICE:
      return <AndroidLogo size={ICON_SIZE} fill="#3A474E" />;
    default:
      return null;
  }
};
interface TestItemProps {
  isActive: boolean;
  onRunTest: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  handleOnSelect: () => void;
  index: number;
  title: string;
  type: TUseCaseType;
  tag: string;
  testTag: string;
}

const componentName = 'c-TestTable';
export const TestItem: FC<PropsWithChildren<TestItemProps>> = props => {
  const {
    isActive,
    onRunTest,
    index,
    title: fullTitle,
    handleOnSelect,
    type,
    tag,
    testTag,
    ...rest
  } = props;
  const c = useClassHelper(componentName);
  const { colors } = useThemeStore();
  const [deviceKey, title] = getDeviceAndTitleFromTestCase(fullTitle);
  const Icon = getDeviceIcon(deviceKey);

  const useGetTestExecution =
    type === USE_CASE ? useGetUseCaseTestsExecution : useGetApiTestExecution;

  const testExecutionKey =
    type === USE_CASE ? 'useCaseTestExecutions' : 'apiTestExecutions';

  const updateTestExecutionKey =
    type === USE_CASE ? 'updateUseCaseTestExecution' : 'updateApiTestExecution';

  const {
    [testExecutionKey]: executions,
    [updateTestExecutionKey]: updateTestExecution,
  } = useTestExecutionsStore();

  const { id, status } = useMemo(
    () => executions[tag]?.[testTag] || ({} as ApiTestExecution),
    [executions?.[tag]?.[testTag]],
  );

  const [shouldResumeTest, setShouldResumeTest] = useState(false);

  const isPending = useMemo(
    () => [PENDING, CREATED, RUNNING].includes(status) || shouldResumeTest,
    [status, shouldResumeTest],
  );
  const isDisabled = useMemo(
    () => [RUNNING, CREATED].includes(status),
    [status],
  );

  const { data: execution, isFetching } = useGetTestExecution(
    id,
    isPending,
    shouldResumeTest,
  );

  const { startPopUpFlow } = useWebsheetPopUp({
    resumeCallback: () => setShouldResumeTest(true),
    cancelCallback: () =>
      updateTestExecution(tag, testTag, { ...execution, status: CANCELLED }),
  });

  useEffect(() => {
    if (execution) {
      updateTestExecution(tag, testTag, execution);

      if (![PENDING].includes(execution.status)) {
        setShouldResumeTest(false);
      }

      startPopUpFlow(execution);
    }
  }, [execution]);

  return (
    <div
      className={c('__row-content', { '--active_row': isActive })}
      role="button"
      tabIndex={0}
      aria-pressed="false"
      onClick={handleOnSelect}
      data-cy={`test-item-${testTag}`}
    >
      <div className={c('__details')}>
        <div className={c('__details__number')}>#{index}</div>
        <Button
          size="xs"
          classes={{ button: c('__run-test-btn') }}
          disabled={isFetching ? true : isDisabled}
          onClick={onRunTest}
          aria-label={`Run ${title}`}
          data-cy={`run-test-button-${testTag}`}
          {...rest}
        >
          <Play size={16} color={colors.white} weight="fill" />
        </Button>
        <span className={c('__details__label')}>{title}</span>
      </div>
      <div className={c('__icons_container')}>
        {Icon && <div className={c('__icon')}>{Icon}</div>}
        <TestIcon
          status={isFetching ? RUNNING : status}
          isFetching={isFetching}
          testTag={testTag}
          type={type}
        />
      </div>
    </div>
  );
};
